import { useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { Users, Plus, Tent } from 'lucide-react'
import { useGroups } from '../hooks/useGroups'
import { usePageTitle } from '../hooks/usePageTitle'
import { LEGACY_PRODUCT_NAME } from '../lib/product'
import { Badge, Button, Card, Dialog, EmptyState, Input, Spinner } from '../components/ui'

export function GroupsScreen() {
  usePageTitle('Your groups', LEGACY_PRODUCT_NAME)
  const { groups, isLoading, error, createGroup } = useGroups()
  const navigate = useNavigate()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [name, setName] = useState('')
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState('')

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    setCreating(true)
    setCreateError('')
    try {
      const group = await createGroup(name.trim())
      setDialogOpen(false)
      setName('')
      navigate({ to: '/steam/groups/$groupId', params: { groupId: group.id } })
    } catch {
      setCreateError('Could not create that group. Try again?')
    } finally {
      setCreating(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* flex-wrap: the CTA drops below the heading whole at tight widths. */}
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl leading-tight text-text-strong">Your groups</h1>
          <p className="mt-1 text-sm text-text-muted">A persistent circle of friends — pick who&apos;s in per night.</p>
        </div>
        <Button onClick={() => setDialogOpen(true)}>
          <Plus className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
          New group
        </Button>
      </header>

      {error && <p className="text-sm text-danger-fg">{error}</p>}

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner size="lg" />
        </div>
      ) : groups.length === 0 ? (
        <EmptyState
          icon={<Tent className="h-10 w-10" strokeWidth={1.5} />}
          title="No groups yet"
          description="Start a group, invite your crew by Steam ID, then gather around the fire."
          action={<Button onClick={() => setDialogOpen(true)}>Create your first group</Button>}
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {groups.map((g) => (
            // Navigation is a LINK, not a div-button (first rule of ARIA):
            // the heading's anchor stretches over the card via after:inset-0,
            // the card carries the hover/focus affordance (§7 ring idiom via
            // has-[]:, so it tracks the link's :focus-visible only).
            <Card
              key={g.id}
              className="relative transition duration-200 ease-out hover:-translate-y-0.5 hover:shadow-warm motion-reduce:hover:translate-y-0 has-[a:focus-visible]:ring-2 has-[a:focus-visible]:ring-ember-400 has-[a:focus-visible]:ring-offset-2 has-[a:focus-visible]:ring-offset-canvas"
            >
              <div className="flex items-center justify-between gap-3">
                {/* min-w-0: flex items refuse to shrink below content width,
                    so without it `truncate` never engages and a long group
                    name pushes the card past the viewport at 390. */}
                <h2 className="min-w-0 truncate text-lg font-semibold text-text-strong">
                  <Link
                    to="/steam/groups/$groupId"
                    params={{ groupId: g.id }}
                    className="focus-visible:outline-none after:absolute after:inset-0 after:rounded-card"
                  >
                    {g.name ?? 'Untitled group'}
                  </Link>
                </h2>
                {/* Member count in the shared neutral chip language (§6.8) —
                    slate register so amber stays meaningful (§1.4). */}
                <Badge icon={<Users className="h-3.5 w-3.5" strokeWidth={1.75} />} className="shrink-0">
                  <span className="tabular-nums">{g.member_count}</span>
                  <span className="sr-only">{g.member_count === 1 ? 'member' : 'members'}</span>
                </Badge>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} title="New group">
        <form onSubmit={handleCreate} className="space-y-4">
          <Input
            label="Group name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="The Thursday crew"
            error={createError}
            autoFocus
          />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={creating}>
              Create
            </Button>
          </div>
        </form>
      </Dialog>
    </div>
  )
}
